/**
 * Template Management Selectors
 * NgRx selectors for admin template state
 */
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { TemplateState } from './template.reducer';
import { CATEGORY_METADATA, STATUS_METADATA } from '../../models/template.models';

/**
 * Feature selector for template state
 */
export const selectTemplateState = createFeatureSelector<TemplateState>('templates');

// ==================== Basic Selectors ====================

export const selectTemplates = createSelector(
  selectTemplateState,
  (state) => state.templates
);

export const selectTemplatesTotal = createSelector(
  selectTemplateState,
  (state) => state.total
);

export const selectTemplatesPage = createSelector(
  selectTemplateState,
  (state) => state.page
);

export const selectTemplatesPageSize = createSelector(
  selectTemplateState,
  (state) => state.pageSize
);

export const selectTemplateStats = createSelector(
  selectTemplateState,
  (state) => state.stats
);

export const selectSelectedTemplate = createSelector(
  selectTemplateState,
  (state) => state.selectedTemplate
);

export const selectSelectedTemplateVersions = createSelector(
  selectTemplateState,
  (state) => state.selectedTemplateVersions
);

export const selectFilters = createSelector(
  selectTemplateState,
  (state) => state.filters
);

// ==================== Loading Selectors ====================

export const selectLoading = createSelector(
  selectTemplateState,
  (state) => state.loading
);

export const selectStatsLoading = createSelector(
  selectTemplateState,
  (state) => state.statsLoading
);

export const selectDetailLoading = createSelector(
  selectTemplateState,
  (state) => state.detailLoading
);

export const selectSaving = createSelector(
  selectTemplateState,
  (state) => state.saving
);

export const selectAnyLoading = createSelector(
  selectLoading,
  selectStatsLoading,
  selectDetailLoading,
  selectSaving,
  (loading, statsLoading, detailLoading, saving) =>
    loading || statsLoading || detailLoading || saving
);

// ==================== Error Selectors ====================

export const selectError = createSelector(
  selectTemplateState,
  (state) => state.error
);

export const selectStatsError = createSelector(
  selectTemplateState,
  (state) => state.statsError
);

export const selectDetailError = createSelector(
  selectTemplateState,
  (state) => state.detailError
);

// ==================== Validation Selectors ====================

export const selectValidation = createSelector(
  selectTemplateState,
  (state) => state.validation
);

export const selectValidating = createSelector(
  selectTemplateState,
  (state) => state.validating
);

export const selectIsJsonValid = createSelector(
  selectValidation,
  (validation) => validation?.valid ?? true
);

export const selectValidationErrors = createSelector(
  selectValidation,
  (validation) => validation?.errors ?? []
);

export const selectValidationWarnings = createSelector(
  selectValidation,
  (validation) => validation?.warnings ?? []
);

// ==================== Derived Selectors ====================

export const selectLastUpdated = createSelector(
  selectTemplateState,
  (state) => state.lastUpdated
);

export const selectPagination = createSelector(
  selectTemplatesTotal,
  selectTemplatesPage,
  selectTemplatesPageSize,
  (total, page, pageSize) => ({
    total,
    page,
    pageSize,
    totalPages: Math.ceil(total / pageSize)
  })
);

export const selectSelectedTemplateFields = createSelector(
  selectSelectedTemplate,
  (template) => template?.fields ?? []
);

export const selectTemplatesWithMetadata = createSelector(
  selectTemplates,
  (templates) => templates.map(template => ({
    ...template,
    categoryMeta: CATEGORY_METADATA[template.category] || CATEGORY_METADATA['other'],
    statusMeta: STATUS_METADATA[template.status] || STATUS_METADATA['draft']
  }))
);

export const selectPendingTemplates = createSelector(
  selectTemplates,
  (templates) => templates.filter(t => t.status === 'pending' || t.status === 'pending_approval')
);

export const selectPendingCount = createSelector(
  selectPendingTemplates,
  (templates) => templates.length
);

